if (!customElements.get('cart-assurance')) {
  customElements.define(
    'cart-assurance',
    class CartAssurance extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.sectionId = this.getAttribute('data-section-id');
        this.linkedVariantId = this.getAttribute('data-linked-variant-id');
        this.allAssuranceIds = (this.getAttribute('data-all-assurance-ids') || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        this.on(this, 'change', this.onChange.bind(this));
        this.on(this, 'click', this.onClick.bind(this));
        if (this.pdfDialog) {
          this.on(this.pdfDialog, 'close', () => {
            document.documentElement.classList.remove('cart-assurance-pdf-open');
          });
        }
        this.onCheckoutListener = this.onCheckoutClick.bind(this);
        document.querySelectorAll('button[name="checkout"]').forEach((button) => {
          this.on(button, 'click', this.onCheckoutListener);
        });

        this.setExpanded(Boolean(this.toggle?.checked));
        this.updateSelectedStyles();
      }

      get toggle() {
        return this.querySelector('.cart-assurance__radio');
      }

      get optionsGroup() {
        return this.querySelector('.cart-assurance__options');
      }

      get details() {
        return this.querySelector('.cart-assurance__details');
      }

      get options() {
        return Array.from(this.querySelectorAll('.cart-assurance__input'));
      }

      get selectedOption() {
        return this.options.find((input) => input.checked) || null;
      }

      setExpanded(enabled) {
        this.classList.toggle('is-expanded', enabled);
        this.options.forEach((option) => {
          option.disabled = !enabled;
        });
      }

      updateSelectedStyles() {
        this.options.forEach((option) => {
          option.closest('.cart-assurance__option')?.classList.toggle('is-selected', option.checked);
        });
      }

      get pdfDialog() {
        return this.querySelector('[data-assurance-pdf-dialog]');
      }

      // On mobile the link is left alone and opens the PDF in a new tab instead of
      // the popup: Safari iOS renders a PDF inside an iframe as a frozen image of
      // page 1 and Chrome Android does not render it at all. Same breakpoint as the
      // CSS, plus any browser without a built-in PDF viewer (e.g. iPad).
      get pdfOpensInNewTab() {
        if (window.matchMedia('(max-width: 767px)').matches) return true;
        return navigator.pdfViewerEnabled === false;
      }

      onClick(event) {
        if (event.target.closest('[data-assurance-remove]')) {
          event.preventDefault();
          if (this.toggle?.checked) this.toggle.click();
          return;
        }

        if (event.target.closest('[data-assurance-pdf-open]')) {
          if (this.pdfOpensInNewTab) return;
          event.preventDefault();
          this.openPdfDialog();
          return;
        }

        if (event.target.closest('[data-assurance-pdf-close]')) {
          event.preventDefault();
          this.closePdfDialog();
          return;
        }

        // Click on the backdrop: the dialog itself is the target (content covers its whole box).
        const dialog = this.pdfDialog;
        if (dialog && dialog.open && event.target === dialog) {
          this.closePdfDialog();
          return;
        }

        this.onBoxClick(event);
      }

      // The whole grey box toggles the assurance while it is collapsed; once expanded
      // only the header row does, so clicking the description or the options never
      // switches it off by accident. Real controls (label, inputs, links, buttons,
      // the option cards) keep their own behaviour.
      onBoxClick(event) {
        const toggle = this.toggle;
        if (!toggle || event.target.closest('label, input, a, button, dialog')) return;

        const zone = this.classList.contains('is-expanded')
          ? this.querySelector('.cart-assurance__summary')
          : this.querySelector('.cart-assurance__inner');
        if (!zone || !zone.contains(event.target)) return;

        toggle.click();
      }

      openPdfDialog() {
        const dialog = this.pdfDialog;
        if (!dialog || dialog.open) return;

        // The PDF is only requested on first open.
        const frame = dialog.querySelector('.cart-assurance__pdf-frame');
        if (frame && !frame.getAttribute('src')) frame.src = frame.dataset.src;

        dialog.showModal();
        document.documentElement.classList.add('cart-assurance-pdf-open');
      }

      closePdfDialog() {
        const dialog = this.pdfDialog;
        if (dialog?.open) dialog.close();
      }

      async onChange(event) {
        const input = event.target;

        if (input.matches('.cart-assurance__radio')) {
          await this.onToggleChange(input.checked);
          return;
        }

        if (!input.matches('.cart-assurance__input')) return;

        if (this.toggle && !this.toggle.checked) {
          this.toggle.checked = true;
          this.setExpanded(true);
        }

        this.updateSelectedStyles();
        await this.syncAssurance(Number(input.dataset.variantId));
      }

      async onToggleChange(enabled) {
        this.setExpanded(enabled);

        if (!enabled) {
          this.options.forEach((option) => {
            option.checked = false;
          });
          this.updateSelectedStyles();
          await this.removeAssurance();
          return;
        }

        const firstOption = this.options[0];
        if (!firstOption) return;

        firstOption.checked = true;
        this.updateSelectedStyles();
        await this.syncAssurance(Number(firstOption.dataset.variantId));
      }

      async onCheckoutClick(event) {
        if (this.toggle && !this.toggle.checked) return;

        const selected = this.selectedOption;
        if (!selected) return;

        const variantId = Number(selected.dataset.variantId);
        const cart = await this.fetchCart();
        const linkedVariantId = this.resolveLinkedVariantId(cart);
        const assuranceItem = this.findAssuranceItem(cart);
        const hasCorrectProperty =
          assuranceItem &&
          String(assuranceItem.variant_id) === String(variantId) &&
          String(this.getLinkedProperty(assuranceItem) || '') === String(linkedVariantId);

        if (hasCorrectProperty) return;

        event.preventDefault();
        event.stopPropagation();

        const synced = await this.syncAssurance(variantId, cart);
        if (!synced) return;

        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
      }

      getLinkedProperty(item) {
        const properties = item?.properties || {};
        return properties._VariantId || properties.variantId || null;
      }

      resolveLinkedVariantId(cart) {
        const preferredId = this.linkedVariantId;
        const items = cart?.items || [];

        if (preferredId) {
          const preferredItem = items.find(
            (item) =>
              String(item.variant_id) === String(preferredId) &&
              !this.isAssuranceVariant(item.variant_id)
          );
          if (preferredItem) return String(preferredItem.variant_id);
        }

        const veloItems = items.filter(
          (item) =>
            !this.isAssuranceVariant(item.variant_id) &&
            String(item.product_type || '').toLowerCase() === 'vélos'
        );

        if (veloItems.length === 0) {
          const fallback = items
            .filter((item) => !this.isAssuranceVariant(item.variant_id))
            .sort((a, b) => Number(b.final_price || b.price || 0) - Number(a.final_price || a.price || 0))[0];
          return fallback ? String(fallback.variant_id) : '';
        }

        veloItems.sort(
          (a, b) => Number(b.final_price || b.price || 0) - Number(a.final_price || a.price || 0)
        );
        return String(veloItems[0].variant_id);
      }

      resolveParentCartItem(cart, linkedVariantId) {
        return (cart?.items || []).find(
          (item) =>
            String(item.variant_id) === String(linkedVariantId) &&
            !this.isAssuranceVariant(item.variant_id)
        );
      }

      isAssuranceVariant(variantId) {
        return this.allAssuranceIds.some(
          (id) => String(id) === String(variantId) || Number(id) === Number(variantId)
        );
      }

      async removeAssurance() {
        this.classList.add('is-loading');

        try {
          const cart = await this.fetchCart();
          const previousItem = this.findAssuranceItem(cart);
          if (!previousItem) return true;

          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:bundled-sections', {
              bubbles: true,
              detail: { sections: sectionsToBundle },
            })
          );

          const body = JSON.stringify({
            id: previousItem.key,
            quantity: 0,
            sections: sectionsToBundle,
          });

          const response = await fetch(theme.routes.cart_change_url, {
            ...theme.utils.fetchConfig(),
            body,
          });
          const parsedState = await response.json();

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-assurance',
            cart: parsedState,
          });

          return true;
        } catch (error) {
          console.error(error);
          return false;
        } finally {
          this.classList.remove('is-loading');
        }
      }

      async syncAssurance(variantId, existingCart) {
        if (!variantId) return false;

        this.classList.add('is-loading');

        try {
          const cart = existingCart || (await this.fetchCart());
          const linkedVariantId = this.resolveLinkedVariantId(cart);
          if (!linkedVariantId) {
            console.error('No linked velo variant found for assurance');
            return false;
          }

          this.linkedVariantId = linkedVariantId;
          const previousItem = this.findAssuranceItem(cart);

          if (previousItem && String(previousItem.variant_id) === String(variantId)) {
            if (String(this.getLinkedProperty(previousItem) || '') === String(linkedVariantId)) {
              return true;
            }

            await this.removeLine(previousItem.key);
          } else if (previousItem) {
            await this.removeLine(previousItem.key);
          }

          await this.addAssurance(variantId, cart, linkedVariantId);
          return true;
        } catch (error) {
          console.error(error);
          return false;
        } finally {
          this.classList.remove('is-loading');
        }
      }

      findAssuranceItem(cart) {
        return (cart.items || []).find((item) => this.isAssuranceVariant(item.variant_id));
      }

      async fetchCart() {
        const response = await fetch(theme.routes.cart_url, {
          ...theme.utils.fetchConfig('json', 'GET'),
        });
        return response.json();
      }

      async removeLine(key) {
        const body = JSON.stringify({ id: key, quantity: 0 });
        const response = await fetch(theme.routes.cart_change_url, {
          ...theme.utils.fetchConfig(),
          body,
        });
        return response.json();
      }

      async addAssurance(variantId, existingCart, linkedVariantId) {
        const cart = existingCart || (await this.fetchCart());
        const parentVariantId = linkedVariantId || this.resolveLinkedVariantId(cart);
        const parentCartItem = this.resolveParentCartItem(cart, parentVariantId);

        if (!parentVariantId) {
          throw new Error('Missing linked product variant id for assurance');
        }

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const itemPayload = {
          id: Number(variantId),
          quantity: 1,
          properties: {
            _VariantId: String(parentVariantId),
          },
        };

        if (parentCartItem?.key) {
          itemPayload.parent_line_key = parentCartItem.key;
        }

        const body = JSON.stringify({
          items: [itemPayload],
          sections: sectionsToBundle,
        });

        const response = await fetch(theme.routes.cart_add_url, {
          ...theme.utils.fetchConfig('javascript'),
          body,
        });
        const parsedState = await response.json();

        if (parsedState.status && parsedState.status >= 400) {
          throw new Error(parsedState.description || parsedState.message || 'Unable to add assurance');
        }

        const cartJson = await this.fetchCart();
        cartJson.sections = parsedState.sections;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-assurance',
          cart: cartJson,
        });

        return cartJson;
      }
    }
  );
}
